// Minimal language picker: a globe icon button in the top-right of the
// green header. Clicking it opens a small popover with all available
// languages. Keeps the header slim so the (potentially long) branding logo
// on the left has as much room as possible.
import React, { useEffect, useRef, useState } from "react";
import { Globe, Check } from "lucide-react";
import { useI18n } from "../lib/i18n";

export const LanguageGlobe = ({ variant = "onGreen" }) => {
    const { lang, setLang, languages, t } = useI18n();
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = (e) => {
            if (!wrapperRef.current) return;
            if (!wrapperRef.current.contains(e.target)) setOpen(false);
        };
        const onEsc = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("keydown", onEsc);
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("keydown", onEsc);
        };
    }, [open]);

    const buttonBase =
        "flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full transition focus-visible:outline-none focus-visible:ring-2";
    const buttonSkin =
        variant === "onGreen"
            ? "bg-white/15 hover:bg-white/25 text-white ring-white/30 focus-visible:ring-white/50"
            : "bg-zinc-100 hover:bg-zinc-200 text-zinc-800 ring-zinc-300 focus-visible:ring-zinc-400";

    return (
        <div className="relative" ref={wrapperRef}>
            <button
                type="button"
                data-testid="language-globe-button"
                aria-label={t("language_label")}
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                className={`${buttonBase} ${buttonSkin}`}
            >
                <Globe className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.25} />
            </button>
            {open && (
                <div
                    data-testid="language-globe-popover"
                    role="menu"
                    className="absolute right-0 top-full mt-2 min-w-[200px] max-h-[70vh] overflow-y-auto rounded-2xl bg-white shadow-2xl ring-1 ring-zinc-200 py-1.5 z-[1200]"
                >
                    {languages.map((l) => {
                        const selected = l.code === lang;
                        return (
                            <button
                                key={l.code}
                                type="button"
                                data-testid={`language-option-${l.code}`}
                                onClick={() => {
                                    setLang(l.code);
                                    setOpen(false);
                                }}
                                role="menuitem"
                                className={`w-full flex items-center justify-between gap-3 px-3.5 py-2 text-left text-sm font-display font-semibold transition ${
                                    selected
                                        ? "bg-zinc-900 text-white"
                                        : "text-zinc-800 hover:bg-zinc-100"
                                }`}
                            >
                                <span className="truncate">{l.label}</span>
                                {selected && (
                                    <Check
                                        className="w-4 h-4 flex-shrink-0"
                                        strokeWidth={3}
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
